import _ from 'lodash';
import { formatItem, formatTypes } from '../../../../global/common/dateFormatting';
import { getLevelMatchez } from '../../cnr/reducers/reducerHelpers/seasonSportsData';
import { createMatchUpdates_data } from '../../cnr/reducers/reducerHelpers/sportsGsData';
import { matchCalcs } from '../../helpers/match';


const parseDate = (dateStr) => {
  const [month, day, year] = dateStr.split('/');
  return new Date(+year, +month - 1, +day);
};

const getSingleLevelMatchDateGroups = (levelMatches) => {
  return _(levelMatches)
    .groupBy(m => m.startDate) // Group by startDate
    .map((matchGroup, startDate) => {
      // Partition matches into regular matches and playoff matches
      const [matches, playoffMatches] = _.partition(matchGroup, match => match.isPlayoff !== true);
      return {
        startDate,
        matches: _.keyBy(matches, '_itemKey'), // Regular matches
        playoffMatches: _.keyBy(playoffMatches, '_itemKey') // Playoff matches
      };
    })
    .sortBy(group => parseDate(group.startDate)) // Sort by startDate
    .value();
};

const getFavMatches = (teamKeys, matches) =>
  _.filter(matches, (match) =>
    _.includes(teamKeys, match?.away?.id) ||
    _.includes(teamKeys, match?.home?.id)
  );

const getLevelMatchDateGroups = (matches) => {

  const today = new Date();

  // Group matches by levels
  const levelMatchDateGroups = _(matches)
    .groupBy('levels')
    .mapValues(levelMatches => {
      return _(levelMatches)
        .groupBy(m => m.startDate)
        .map((matchGroup, startDate) => {
          // Partition matches into regular matches and playoff matches
          const [matches, playoffMatches] = _.partition(matchGroup, match => match.isPlayoff !== true);
          return {
            startDate,
            matches: _.keyBy(matches, '_itemKey'), // Regular matches
            playoffMatches: _.keyBy(playoffMatches, '_itemKey'), // Playoff matches
            matches_playoffs: _.keyBy(playoffMatches, '_itemKey') // Playoff matches
          };
        })
        .sortBy(obj => parseDate(obj.startDate))
        .value();
    })
    .value();

  // Calculate the last startDate for each level
  const levelLatestStartDates = _(levelMatchDateGroups)
    .mapValues(levelGroups => {
      const valid = levelGroups.filter(({ startDate }) => {
        return parseDate(startDate) <= today.getTime();
      });

      if (valid.length > 0) {
        return _.maxBy(valid, ({ startDate }) => parseDate(startDate)).startDate;
      }

      return null;
    })
    .value();


  // Calculate the latest startDate for each level
  const levelLastStartDates = _(levelMatchDateGroups)
    .mapValues(levelGroups => {
      const latest = _.maxBy(levelGroups, ({ startDate }) => parseDate(startDate));
      return latest?.startDate ?? null;
    })
    .value();


  return { levelMatchDateGroups, levelLatestStartDates, levelLastStartDates };
};


// export const getLevelMatchDateGroups = (matches) =>
//   _(matches)
//     .groupBy('levels') // 1. Group by levels
//     .mapValues(levelMatches => {
//       const groupedByDate = _(levelMatches)
//         .groupBy(m => m.startDate) // 2. Group by startDate
//         .map((matchGroup, startDate) => ({
//           startDate,
//           matches: _.keyBy(matchGroup, '_itemKey') // 3. Convert array to keyed object
//         }))
//         .sortBy(obj => parseDate(obj.startDate)) // 4. Sort with reliable date parsing
//         .value();

//       // 5. Convert back to an ordered object
//       return _.fromPairs(groupedByDate.map(d => [d.startDate, d.matches]));
//     })
//     .value();

const getMatchDateKeys = (matchDateGroups) => {
  const today = new Date();
  const _today = formatItem(formatTypes.shortestDate, today);

  let _todaysMatchCount;
  const matchDateKeys = [];
  const matchDateKeys_count = {};

  if (matchDateGroups) {
    Object.entries(matchDateGroups).forEach(([startDate, matches]) => {
      matchDateKeys.push(startDate);
      const count = Object.keys(matches).length;
      matchDateKeys_count[startDate] = count;

      if (startDate === _today) {
        _todaysMatchCount = count;
      }
    });
  }

  return { matchDateKeys, matchDateKeys_count, _todaysMatchCount };
};

const getTeamDirectMatches = (matches, selectedTeams) => {

  const [teamA, teamB] = selectedTeams ?? {};

  return _.filter(matches, match => {
    const homeId = _.get(match, 'home.id');
    const awayId = _.get(match, 'away.id');
    const isComplete = _.get(match, '_results.complete', false);

    return (
      isComplete &&
      (
        (homeId === teamA && awayId === teamB) ||
        (homeId === teamB && awayId === teamA)
      )
    );
  });
};

const getMatchesForTeam = (matches, teamId, remainingOnly) =>
  _.filter(matches, m => {
    const isTeamMatch = m.home?.id === teamId || m.away?.id === teamId;
    if (!isTeamMatch) return false;

    if (remainingOnly) {
      const [month, day, year] = m.startDate.split('/').map(Number);
      const matchDate = new Date(year, month - 1, day);

      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset to start of today

      return matchDate >= today;
    }

    return true;
  });


const getMatchesToday = (matches) => {
  const today = new Date();
  const todayStr = today.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  return _.filter(matches, m => m.startDate === todayStr);
};

// export const getTeamRecord = (matches, teamId) =>
//   _(matches)
//     .filter(m => m.result?.score)
//     .reduce((acc, m) => {
//       const isHome = m.home?.id === teamId;
//       const isAway = m.away?.id === teamId;
//       const homeScore = m.result?.score.home;
//       const awayScore = m.result?.score.away;

//       if (isHome || isAway) {
//         const teamScore = isHome ? homeScore : awayScore;
//         const oppScore = isHome ? awayScore : homeScore;

//         if (teamScore > oppScore) acc.wins += 1;
//         else if (teamScore < oppScore) acc.losses += 1;
//         else acc.draws += 1;
//       } 
//       return acc;
//     }, { wins: 0, losses: 0, draws: 0 });

const getUniqueTeams = (matches) =>
  _(matches)
    .flatMap(m => [m.home, m.away])
    .uniqBy('id')
    .value();

const getMatchesInRange = (matches, startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return _.filter(matches, m => {
    const date = new Date(m.startDate);
    return date >= start && date <= end;
  });
};

const groupByWeek = (matches) =>
  _(matches)
    .groupBy(m => {
      const d = new Date(m.startDate);
      const year = d.getFullYear();
      const week = Math.ceil((((d - new Date(year, 0, 1)) / 86400000) + new Date(year, 0, 1).getDay() + 1) / 7);
      return `${year}-W${week}`;
    })
    .value();

const findDuplicateMatchups = (matches) =>
  _(matches)
    .groupBy(m => [m.home?.id, m.away?.id].sort().join('-'))
    .pickBy(g => g.length > 1)
    .value();

const getPastMatchesWithoutResult = (matches) => {
  const now = new Date();
  return _.filter(matches, m => {
    const startDate = new Date(m.startDate);
    const hasScore = _.has(m, '_results.score.home') && _.has(m, '_results.score.away');
    return startDate < now && !hasScore;
  });
};

const filterByTeamIdSorted = (matches, teamId) =>
  _(matches)
    .filter(m => m.home?.id === teamId || m.away?.id === teamId)
    .sortBy(m => new Date(m.startDate))
    .value();

const groupByLevelSectionSorted = (matches) =>
  _(matches)
    .orderBy([m => m.levels, m => m.section, m => new Date(m.startDate)], ['desc', 'asc', 'asc'])
    .groupBy(m => `${m.levels}.${m.section}`)
    .value();

const groupByLevelSectionForDate = (matches, date) =>
  _(matches)
    .filter(m => m.startDate === date)
    .orderBy([m => m.levels, m => m.section, m => new Date(m.startDate)], ['desc', 'asc', 'asc'])
    .groupBy(m => `${m.levels}.${m.section}`)
    .value();

const getLatestMatches = (matches, teamId = null, latestCount = 5) =>
  _(matches)
    .filter(m => new Date(m.startDate) <= new Date() && m.results)
    .thru(ms => teamId ? _.filter(ms, m => m.home?.id === teamId || m.away?.id === teamId) : ms)
    .orderBy(m => new Date(m.startDate), 'desc')
    .map(m => {
      if (!teamId) return m;
      return {
        ...m,
        opponent: m.home?.id === teamId ? m.away : m.home
      };
    })
    .take(latestCount)
    .value();

const getMissingMatches = (matches) => {
  const _matches = _.pickBy(matches, match =>
    _.get(match, '_status.isMissing') === true &&
    !_.has(match, '_results_matchDay')   // exclude if results day exists
  );
  const { _levelDateGroups } = getLevelMatchez(_matches);
  return _levelDateGroups
}

const getMatchDayMatches = (matches, matches_matchDay, forUpdate) => {
  const _matches = _.pick(matches, _.keys(matches_matchDay));
  const { _levelDateGroups } = getLevelMatchez(_matches);
  if (forUpdate) {
    const matchUpdates_data = {}
    createMatchUpdates_data(_matches, matchUpdates_data)
    return matchUpdates_data
  }
  return _levelDateGroups
}

const getNextMatch = (matches, teamId) =>
  _(matches)
    .filter(m => m.home?.id === teamId || m.away?.id === teamId)
    .filter(m => parseDate(m.startDate) >= new Date())
    .filter(m => !m.results)
    .orderBy(m => parseDate(m.startDate), 'asc')
    .first();

const getFilterMatches = (matches, teamId, matchFilter) => {
  const { selectedLocation, selectedResult, selectedSection } = matchFilter;

  const matches_filtered = _.filter(matches, (match) => {
    const isHome = match.home?.id === teamId;
    const isAway = match.away?.id === teamId;

    // First filter: must involve the team
    if (!isHome && !isAway) return false;

    let include = true;

    // Location filter
    if (selectedLocation === 'home') include = include && isHome;
    if (selectedLocation === 'away') include = include && isAway;

    // Section filter
    if (selectedSection) {
      const isSection = match.home?.sections === match.away?.sections;

      switch (selectedSection) {
        case 'non':
          include = include && !isSection;
          break;
        default:
          include = include && isSection;
          break;
      }
    }

    // Result filter
    const homeScore = _.get(match, '_results.score.home');
    const awayScore = _.get(match, '_results.score.away');

    const hasResult =
      _.isNumber(homeScore) &&
      _.isNumber(awayScore) &&
      homeScore !== null &&
      awayScore !== null;

    if (selectedResult) {
      if (hasResult) {
        const didTeamWin =
          (isHome && homeScore > awayScore) ||
          (isAway && awayScore > homeScore);

        const didTeamLose =
          (isHome && homeScore < awayScore) ||
          (isAway && awayScore < homeScore);

        if (selectedResult === 'win') include = include && didTeamWin;
        if (selectedResult === 'loss') include = include && didTeamLose;
      } else {
        include = false;
      }
    }

    return include;
  });

  return matches_filtered
};

const getTodaysLevelMatches = (levelDateGroups, addedHours, selectedDate) => {
  let adjustedNow;

  if (selectedDate) {
    // Parse selectedDate (assumes 'MM/DD/YYYY' format)
    const [month, day, year] = selectedDate.split('/').map(Number);
    adjustedNow = new Date(year, month - 1, day);
  } else {
    const now = new Date();
    adjustedNow = addedHours ? new Date(now.getTime() - addedHours * 60 * 60 * 1000) : now;
  }
  const todayStr = adjustedNow.toLocaleDateString('en-US', {
    // timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  // Use _.mapValues to transform the levelDateGroups object
  const xxx = _.mapValues(levelDateGroups, (dateGroups) =>
    _.filter(dateGroups, (group) => group.startDate === todayStr)
  );

  return xxx;
};

const matchesWithPendings = (matches) => _.pickBy(matches, (match) => {
  const hasResultObject = _.has(match, 'pending');
  const hasHomeObject = _.has(match, 'home');
  const hasAwayObject = _.has(match, 'away');
  return hasResultObject && (!hasHomeObject || !hasAwayObject);
});


const matchesWithResults = (matches) => _.pickBy(matches, (match) => {
  const hasResultObject = _.has(match, 'results');
  return hasResultObject;
});

const getPendingMatches = (matches_info, forSchedule) => {

  const { playoffMatches } = matches_info ?? {}

  const getPendings = () => {

    const _playoffMatches = playoffMatches ? _.cloneDeep(playoffMatches) : {}
    const _matchesWithResults = matchesWithResults(_playoffMatches)
    const _matchesWithPendings = matchesWithPendings(_playoffMatches)

    _.forEach(_matchesWithPendings, (mp) => {

      const { pending, levels, isConsolation } = mp;
      const { home, away } = pending;

      const _match_home = _.find(_matchesWithResults, (m) =>
        _.get(m, 'levels') === levels &&
        _.get(m, 'matchNumber') === home?.matchNumber &&
        _.get(m, 'poolKey') === home?.poolKey &&
        _.has(m, '_results.winner')
      );

      const _match_away = _.find(_matchesWithResults, (m) =>
        _.get(m, 'levels') === levels &&
        _.get(m, 'matchNumber') === away?.matchNumber &&
        _.get(m, 'poolKey') === home?.poolKey &&
        _.has(m, '_results.winner')
      );

      if (_match_home) {
        mp.home = isConsolation ? _match_home._results.loser : _match_home._results.winner;
        mp._pendingUpdate = true;
      }

      if (_match_away) {
        mp.away = isConsolation ? _match_away._results.loser : _match_away._results.winner;
        mp._pendingUpdate = true;
      }
    });

    const mps = {}

    const mtu = _.filter(_matchesWithPendings, { _pendingUpdate: true })

    _.forEach(mtu, (mp) => {
      matchCalcs.calcVballMatch(mp)
      mps[mp._itemKey] = mp
    })

    const { _levelDateGroups } = getLevelMatchez(mps);

    return forSchedule ? _levelDateGroups : mps
  }

  return getPendings()

}


const getPlayoffMatches = (matches) =>
  _.pickBy(matches, match => match._matchCollectionKey === 'playoffs');

// 🔁 Optionally: group them into a single helper object (like you requested)
export const matchFilterHelpers = {
  filterByTeamIdSorted,
  findDuplicateMatchups,
  getFavMatches,
  getFilterMatches,
  getLatestMatches,
  getLevelMatchDateGroups,
  getMatchDateKeys,
  getMatchDayMatches,
  getMatchesForTeam,
  getMatchesInRange,
  getMatchesToday,
  getMissingMatches,
  getNextMatch,
  getPastMatchesWithoutResult,
  getPendingMatches,
  getPlayoffMatches,
  getSingleLevelMatchDateGroups,
  getTeamDirectMatches,
  getTodaysLevelMatches,
  getUniqueTeams,
  groupByLevelSectionForDate,
  groupByLevelSectionSorted,
  groupByWeek,
  matchesWithPendings,
  matchesWithResults,
}; 